import React, { useState, useCallback, useEffect } from "react"
import { BsTrash3Fill } from "react-icons/bs"
import { IoAddCircle } from "react-icons/io5"
import { FaExclamationTriangle } from "react-icons/fa"
import DropdownCombobox from "./CustomCalendar/FormElements/DropdownCombobox"
import { timeStringToMinutes, convertMinutesToMilitaryTime } from "../../utils/generalUtils"
import useLogging from "../../hooks/useLogging"

// Helper function to check if there are default nurses
const hasDefaultNurses = nurses => {
	return nurses.some(nurse => nurse.nurseName?.toUpperCase().includes("DEFAULT"))
}

const allRoleOptions = [{ nurseRole: "Charge" }, { nurseRole: "RN" }, { nurseRole: "LVN" }]

// Helper functions to handle time format conversion
const formatTimeForDisplay = timeValue => {
	// If it's already in HH:MM format, return as-is
	if (typeof timeValue === "string" && timeValue.includes(":")) {
		return timeValue
	}
	// If it's in minutes format, convert to HH:MM
	if (typeof timeValue === "number") {
		return convertMinutesToMilitaryTime(timeValue)
	}
	return timeValue || ""
}

const formatTimeForStorage = timeValue => {
	// If it's in HH:MM format, convert to minutes
	if (typeof timeValue === "string" && timeValue.includes(":")) {
		return timeStringToMinutes(timeValue)
	}
	// If it's already in minutes format, return as-is
	return timeValue
}

export const defaultShifts = [
	{ label: "7:30 am - 4:00 pm", start: "07:30", end: "16:00" },
	{ label: "7:30 am - 6:00 pm", start: "07:30", end: "18:00" },
	{ label: "7:30 am - 8:00 pm", start: "07:30", end: "20:00" },
	{ label: "Custom Shift", start: "", end: "" }
]

const StaffingEntryTableRow = ({
	nurse,
	index,
	isAddRow,
	onNameChange,
	onRoleChange,
	onTimeChange,
	onShiftChange,
	onDelete,
	onAdd,
	selectedNurseName,
	setSelectedNurseName,
	selectedRole,
	setSelectedRole,
	selectedShiftStart,
	setSelectedShiftStart,
	selectedShiftEnd,
	setSelectedShiftEnd,
	selectedShift,
	setSelectedShift,
	errors,
	setErrors,
	settingsNurses,
	tableNurses,
	sandbox,
	readOnly,
	roleOptions
}) => {
	const logging = useLogging()

	// Filter out nurses that are already in the table, unless it's the current nurse being edited
	const availableNurses = settingsNurses.filter(settingsNurse => {
		// If editing an existing row, allow the current nurse to remain in options
		if (!isAddRow && nurse && nurse.nurseName === settingsNurse.nurseName) {
			return true
		}
		// Otherwise, only include nurses not already in the table
		return !tableNurses.some(tableNurse => tableNurse.nurseName === settingsNurse.nurseName)
	})

	// For default nurses (not in settingsNurses), add them to options so they show in dropdown
	// but they won't be selectable since they're not real nurses
	const isCurrentNurseDefault =
		nurse && !settingsNurses.some(sn => sn.nurseName === nurse.nurseName)
	const nurseOptions = availableNurses
		.concat(isCurrentNurseDefault ? [{ ...nurse, id: `${nurse.id}-default` }] : [])
		.sort((a, b) => a.nurseName.localeCompare(b.nurseName))
	const [showTooltip, setShowTooltip] = useState(false)

	const handleNurseChange = useCallback(
		value => {
			const selectedNurse = settingsNurses.find(nurse => nurse.nurseName === value)
			if (selectedNurse) {
				if (isAddRow) {
					if (selectedNurse?.nurseName !== selectedNurseName) {
						setSelectedNurseName(selectedNurse?.nurseName)
					}
					// Auto-fill the role from the nurse's roster role (RN/LVN)
					const derivedRole = roleOptions.find(role =>
						selectedNurse.nurseRoles?.includes(role.nurseRole)
					)
					if (derivedRole) {
						setSelectedRole(derivedRole.nurseRole)
						setErrors(prev => ({ ...prev, role: "" }))
					}
				} else {
					onNameChange(index, selectedNurse?.nurseName)
				}
				setErrors(prev => ({ ...prev, nurseName: "" }))
				logging.info("Nurse changed", {
					nurseName: selectedNurse?.nurseName,
					index: index,
					isAddRow: isAddRow
				})
			}
		},
		[
			isAddRow,
			settingsNurses,
			onNameChange,
			index,
			selectedNurseName,
			setSelectedNurseName,
			setSelectedRole,
			roleOptions,
			setErrors,
			logging
		]
	)

	return (
		<tr>
			<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
				<div className="relative">
					<div className="relative mt-2" style={{ position: "static" }}>
						<DropdownCombobox
							label="Select Nurse Assigned..."
							options={nurseOptions}
							value={isAddRow ? selectedNurseName : nurse?.nurseName}
							onChange={handleNurseChange}
							displayKey={nurse => nurse.nurseName}
							valueKey="nurseName"
							sandbox={sandbox}
							selectTextOnFocus={true}
							readOnly={readOnly}
						/>
						{errors.nurseName && (
							<p className="mt-1 text-sm text-red-600">{errors.nurseName}</p>
						)}
					</div>
				</div>
			</td>
			<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
				<div className="relative">
					<div className="relative mt-2" style={{ position: "static" }}>
						<DropdownCombobox
							label="Select Role..."
							options={roleOptions}
							value={isAddRow ? selectedRole : nurse.nurseRole}
							onChange={value => {
								if (isAddRow) {
									setSelectedRole(value)
									setErrors(prev => ({ ...prev, role: "" }))
								} else {
									onRoleChange(index, value)
								}
							}}
							displayKey={option => option.nurseRole}
							valueKey="nurseRole"
							defaultValue=""
							sandbox={sandbox}
							selectTextOnFocus={true}
							readOnly={readOnly}
						/>
						{isAddRow && errors.role && (
							<p className="mt-1 text-sm text-red-600">{errors.role}</p>
						)}
					</div>
				</div>
			</td>
			<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
				<div className="flex items-center space-x-2">
					<DropdownCombobox
						label="Select Default Shift..."
						options={defaultShifts}
						value={
							isAddRow
								? selectedShift
								: (() => {
										const nurseStartTime = formatTimeForDisplay(
											nurse.scheduledStartTime
										)
										const nurseEndTime = formatTimeForDisplay(
											nurse.scheduledEndTime
										)
										const matchingShift = defaultShifts.find(
											shift =>
												shift.start === nurseStartTime &&
												shift.end === nurseEndTime
										)
										return matchingShift ? matchingShift.label : "Custom Shift"
									})()
						}
						onChange={shift => {
							const matchedShift = defaultShifts.find(
								defaultShift => defaultShift.label === shift
							)
							if (isAddRow) {
								if (matchedShift) {
									setSelectedShiftStart(matchedShift.start)
									setSelectedShiftEnd(matchedShift.end)
									setErrors(prev => ({ ...prev, shiftStart: "", shiftEnd: "" }))
									setSelectedShift(shift)
								}
							} else {
								onShiftChange(index, shift)
							}
						}}
						displayKey={shift => shift.label}
						valueKey="label"
						defaultValue="Custom Shift"
						sandbox={sandbox}
						selectTextOnFocus={true}
						readOnly={readOnly}
					/>
					<div className={`flex flex-col ${readOnly ? "opacity-50" : ""}`}>
						<label className="text-xs text-gray-500">Start</label>
						<input
							type="time"
							value={
								isAddRow
									? selectedShiftStart
									: formatTimeForDisplay(nurse.scheduledStartTime)
							}
							onChange={e => {
								if (isAddRow) {
									setSelectedShiftStart(e.target.value)
									setSelectedShift("Custom Shift")
									setErrors(prev => ({ ...prev, shiftStart: "" }))
								} else {
									onTimeChange(index, "scheduledStartTime", e.target.value)
								}
							}}
							className="block w-36 rounded-md border-gray-300 py-1.5 text-gray-900 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
							disabled={readOnly}
						/>
						{isAddRow && errors.shiftStart && (
							<p className="mt-1 text-sm text-red-600">{errors.shiftStart}</p>
						)}
					</div>
					<div className={`flex flex-col ${readOnly ? "opacity-50" : ""}`}>
						<label className="text-xs text-gray-500">End</label>
						<input
							type="time"
							value={
								isAddRow
									? selectedShiftEnd
									: formatTimeForDisplay(nurse.scheduledEndTime)
							}
							onChange={e => {
								if (isAddRow) {
									setSelectedShiftEnd(e.target.value)
									setSelectedShift("Custom Shift")
									setErrors(prev => ({ ...prev, shiftEnd: "" }))
								} else {
									onTimeChange(index, "scheduledEndTime", e.target.value)
								}
							}}
							className="block w-36 rounded-md border-gray-300 py-1.5 text-gray-900 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
							disabled={readOnly}
						/>
						{isAddRow && errors.shiftEnd && (
							<p className="mt-1 text-sm text-red-600">{errors.shiftEnd}</p>
						)}
					</div>
				</div>
			</td>
			<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
				{/* Invisible label mirrors the shift cell's "Start"/"End" labels so the
				    button lines up with the input boxes instead of riding higher. */}
				<div className="flex flex-col">
					<label className="text-xs text-gray-500 invisible" aria-hidden="true">
						Action
					</label>
					{isAddRow ? (
						<button
							onClick={onAdd}
							className="py-2 px-4 w-24 bg-green-50 border border-green-200 text-green-600 rounded-lg hover:bg-green-100 flex items-center justify-center gap-2"
						>
							<IoAddCircle className="h-4 w-4" />
							Add
						</button>
					) : (
						<div className="relative">
							<button
								onClick={() => {
									if (readOnly) {
										setShowTooltip(!showTooltip)
									} else {
										onDelete(index)
									}
								}}
								className={`py-2 px-4 w-24 bg-red-50 border border-red-200 text-red-600 rounded-lg hover:bg-red-100 flex items-center justify-center gap-2 ${
									readOnly ? "opacity-50 cursor-not-allowed" : ""
								}`}
								disabled={readOnly}
							>
								<BsTrash3Fill className="h-4 w-4" />
								Delete
							</button>
						</div>
					)}
				</div>
			</td>
		</tr>
	)
}

const StaffingEntryTable = ({
	nurses,
	onAddNurse,
	onUpdateNurse,
	onDeleteNurse,
	settingsNurses,
	openTime,
	closeTime,
	readOnly = false,
	sandbox = false,
	showAddRow = true,
	title = "Nurse Schedule",
	containerClassName = "mt-6 flex flex-col",
	allowedRoles = ["Charge", "RN", "LVN"], // Default includes all roles
	defaultShiftByRole // Optional: { RN: "<shift label>", LVN: "<shift label>" } — pre-fill the shift once a role is chosen. Pass a STABLE reference.
}) => {
	const logging = useLogging()

	// Filter role options based on allowedRoles prop
	const roleOptions = allRoleOptions.filter(role => allowedRoles.includes(role.nurseRole))

	// Form state for adding nurses
	const [selectedNurseName, setSelectedNurseName] = useState("")
	const [selectedRole, setSelectedRole] = useState("")
	const [selectedShiftStart, setSelectedShiftStart] = useState("")
	const [selectedShiftEnd, setSelectedShiftEnd] = useState("")
	const [selectedShift, setSelectedShift] = useState("")
	const [errors, setErrors] = useState({ nurseName: "", role: "", shiftStart: "", shiftEnd: "" })

	// When a role is selected (incl. auto-filled from the nurse), default the
	// shift to that role's configured shift, e.g. RN -> 10-hr, LVN -> 8-hr.
	useEffect(() => {
		if (!selectedRole || !defaultShiftByRole) return
		const shift = defaultShifts.find(s => s.label === defaultShiftByRole[selectedRole])
		if (!shift) return
		setSelectedShift(shift.label)
		setSelectedShiftStart(shift.start)
		setSelectedShiftEnd(shift.end)
		setErrors(prev => ({ ...prev, shiftStart: "", shiftEnd: "" }))
	}, [selectedRole, defaultShiftByRole])

	const handleNurseAdd = () => {
		// Reset previous errors
		setErrors({ nurseName: "", role: "", shiftStart: "", shiftEnd: "" })

		let hasErrors = false

		// Validate nurse name (the dropdown emits null when cleared, so guard truthiness)
		if (!selectedNurseName) {
			setErrors(prev => ({ ...prev, nurseName: "Please select a nurse" }))
			logging.info("No nurse selected")
			hasErrors = true
		}

		// Check for duplicate nurse
		const isDuplicate = nurses.some(nurse => nurse.nurseName === selectedNurseName)
		if (isDuplicate) {
			setErrors(prev => ({ ...prev, nurseName: "This nurse is already scheduled" }))
			logging.info("Duplicate nurse selected")
			hasErrors = true
		}

		// Validate role (the dropdown emits null when cleared, so guard truthiness)
		if (!selectedRole) {
			setErrors(prev => ({ ...prev, role: "Please select a role" }))
			logging.info("No role selected")
			hasErrors = true
		}

		// Validate shift times
		if (!selectedShiftStart) {
			setErrors(prev => ({ ...prev, shiftStart: "Please select a start time" }))
			logging.info("No shift start time selected")
			hasErrors = true
		}

		if (!selectedShiftEnd) {
			setErrors(prev => ({ ...prev, shiftEnd: "Please select an end time" }))
			logging.info("No shift end time selected")
			hasErrors = true
		}

		if (hasErrors) return

		// Convert times to minutes for comparison
		const shiftStartMinutes = timeStringToMinutes(selectedShiftStart)
		const shiftEndMinutes = timeStringToMinutes(selectedShiftEnd)
		const openTimeMinutes = timeStringToMinutes(openTime)
		const closeTimeMinutes = timeStringToMinutes(closeTime)

		// Enhanced time validations with specific error messages
		if (shiftStartMinutes < openTimeMinutes - 60) {
			setErrors(prev => ({
				...prev,
				shiftStart: `Shift cannot start before clinic opening time (${openTime})`
			}))
			logging.info("Shift start time before clinic opening time")
			hasErrors = true
		}

		if (shiftEndMinutes > closeTimeMinutes + 60) {
			setErrors(prev => ({
				...prev,
				shiftEnd: `Shift cannot end after clinic closing time (${closeTime})`
			}))
			logging.info("Shift end time after clinic closing time")
			hasErrors = true
		}

		if (shiftEndMinutes <= shiftStartMinutes) {
			setErrors(prev => ({ ...prev, shiftEnd: "Shift end time must be after start time" }))
			logging.info("Shift end time must be after start time")
			hasErrors = true
		}

		if (hasErrors) return

		const selectedNurse = settingsNurses.find(nurse => nurse.nurseName === selectedNurseName)

		const nurseToAdd = {
			nurseName: selectedNurse.nurseName,
			id: selectedNurse.id,
			nurseRole: selectedRole,
			nurseId: selectedNurse.nurseId,
			nurseEmail: selectedNurse.nurseEmail,
			scheduledStartTime: formatTimeForStorage(selectedShiftStart),
			scheduledEndTime: formatTimeForStorage(selectedShiftEnd),
			actualStartTime: null,
			actualEndTime: null,
			scheduledLunchBreakStart: null,
			scheduledLunchBreakEnd: null,
			actualLunchBreakStart: null,
			actualLunchBreakEnd: null,
			defaultShiftLength: selectedShift
		}

		onAddNurse(nurseToAdd)

		// Reset selection state; the role-driven effect re-fills the shift once
		// the next nurse (and role) is picked.
		setSelectedNurseName("")
		setSelectedRole("")
		setSelectedShiftStart("")
		setSelectedShiftEnd("")
		setSelectedShift("")
	}

	const handleNameChange = (index, value) => {
		const selectedNurse = settingsNurses.find(nurse => nurse.nurseName === value)

		if (!selectedNurse) {
			setErrors(prev => ({ ...prev, nurseName: "Nurse not found" }))
			logging.info("Nurse not found")
			return
		}

		const updatedNurse = {
			...nurses[index],
			nurseId: selectedNurse.nurseId,
			nurseName: selectedNurse.nurseName,
			nurseEmail: selectedNurse.nurseEmail,
			id: selectedNurse.id
		}

		onUpdateNurse(index, updatedNurse)
	}

	const handleRoleChange = (index, value) => {
		const updatedNurse = { ...nurses[index], nurseRole: value }
		onUpdateNurse(index, updatedNurse)
	}

	const handleTimeChange = (index, field, value) => {
		const nurse = nurses[index]

		// Convert the new time value to storage format (minutes)
		const storageValue = formatTimeForStorage(value)

		// Check if the new times match any default shift (convert nurse times to display format for comparison)
		const currentStartTime =
			field === "scheduledStartTime" ? value : formatTimeForDisplay(nurse.scheduledStartTime)
		const currentEndTime =
			field === "scheduledEndTime" ? value : formatTimeForDisplay(nurse.scheduledEndTime)

		const matchingShift = defaultShifts.find(
			shift => shift.start === currentStartTime && shift.end === currentEndTime
		)

		const updatedNurse = {
			...nurse,
			[field]: storageValue,
			defaultShiftLength: matchingShift ? matchingShift.label : "Custom Shift"
		}
		onUpdateNurse(index, updatedNurse)
	}

	const handleShiftChange = (index, value) => {
		const matchedShift = defaultShifts.find(shift => shift.label === value)
		const nurse = nurses[index]

		const updatedNurse = {
			...nurse,
			defaultShiftLength: value ? value : "Custom Shift",
			scheduledStartTime:
				matchedShift && matchedShift.start
					? formatTimeForStorage(matchedShift.start)
					: nurse.scheduledStartTime,
			scheduledEndTime:
				matchedShift && matchedShift.end
					? formatTimeForStorage(matchedShift.end)
					: nurse.scheduledEndTime
		}
		onUpdateNurse(index, updatedNurse)
	}

	// Calculate role counts for display
	const roleCounts = nurses.reduce((acc, nurse) => {
		acc[nurse.nurseRole] = (acc[nurse.nurseRole] || 0) + 1
		return acc
	}, {})

	const totalNurses = nurses.length

	return (
		<div className={containerClassName}>
			<h2 className="text-2xl font-semibold text-gray-900 flex items-center justify-center">
				{title}
			</h2>

			<div className="mb-4">
				<p className="text-lg">
					Total Nurses: {totalNurses} • Role Breakdown:{" "}
					{Object.entries(roleCounts).map(([role, count], index) => (
						<span key={role}>
							{role}: {count}
							{index < Object.entries(roleCounts).length - 1 ? " • " : ""}
						</span>
					))}
				</p>
			</div>

			{/* Default Nurses Warning Banner */}
			{hasDefaultNurses(nurses) && (
				<div className="mb-6 bg-amber-50 border border-amber-200 p-4 shadow-sm">
					<div className="text-center">
						<p className="text-amber-800 font-medium flex items-center justify-center">
							<FaExclamationTriangle className="text-amber-500 mr-2 flex-shrink-0" />
							Schedule includes default nurses
						</p>
						<p className="text-amber-700 text-sm mt-1">
							Please update the nurse staffing when possible to assign actual nurses
							for this schedule.
						</p>
					</div>
				</div>
			)}
			<div>
				<div className="min-h-max">
					<table className="min-w-full divide-y divide-gray-200 mt-4 table-auto">
						<thead className="bg-gray-50">
							<tr>
								<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
									Nurse Name
								</th>
								<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
									Role
								</th>
								<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
									Shift
								</th>
								<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
									Actions
								</th>
							</tr>
						</thead>
						<tbody className="bg-white divide-y divide-gray-200">
							{nurses &&
								nurses.map((nurse, index) => (
									<StaffingEntryTableRow
										key={index}
										nurse={nurse}
										index={index}
										isAddRow={false}
										onNameChange={handleNameChange}
										onRoleChange={handleRoleChange}
										onTimeChange={handleTimeChange}
										onShiftChange={handleShiftChange}
										onDelete={() => onDeleteNurse(index)}
										settingsNurses={settingsNurses}
										errors={errors}
										setErrors={setErrors}
										tableNurses={nurses}
										sandbox={sandbox}
										readOnly={readOnly}
										roleOptions={roleOptions}
									/>
								))}
							{showAddRow && !readOnly && (
								<StaffingEntryTableRow
									isAddRow={true}
									onAdd={handleNurseAdd}
									selectedNurseName={selectedNurseName}
									setSelectedNurseName={setSelectedNurseName}
									selectedRole={selectedRole}
									setSelectedRole={setSelectedRole}
									selectedShiftStart={selectedShiftStart}
									setSelectedShiftStart={setSelectedShiftStart}
									selectedShiftEnd={selectedShiftEnd}
									setSelectedShiftEnd={setSelectedShiftEnd}
									selectedShift={selectedShift}
									setSelectedShift={setSelectedShift}
									errors={errors}
									setErrors={setErrors}
									settingsNurses={settingsNurses}
									tableNurses={nurses}
									sandbox={sandbox}
									readOnly={false}
									roleOptions={roleOptions}
								/>
							)}
						</tbody>
					</table>
				</div>
			</div>
		</div>
	)
}

export default StaffingEntryTable
